import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Check } from "lucide-react";
import { Link } from "react-router-dom";
import clsx from "clsx";

export interface DropdownItem {
  label: string;
  value?: string;
  icon?: React.ReactNode;
  href?: string; // for nav links
  disabled?: boolean;
  external?: boolean;
}

interface DropdownProps {
  triggerLabel: string | React.ReactNode;
  items: DropdownItem[];
  onSelect?: (value: string) => void;
  selectedValue?: string;
  align?: "start" | "center" | "end";
  width?: string; // tailwind width e.g. w-48
  className?: string;
  hideArrow?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  triggerLabel,
  items,
  onSelect,
  selectedValue,
  align = "start",
  width = "w-48",
  className,
  hideArrow
}) => {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={clsx(
            "inline-flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#7b2a40]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7b2a40]",
            className
          )}
        >
          {triggerLabel}
          {!hideArrow && <ChevronDown size={14} className="opacity-70 mt-[1px]" />}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          className={clsx(
            "z-50 mt-1 rounded-md border border-gray-200 bg-white shadow-lg p-1",
            "focus:outline-none",
            width
          )}
        >
          {items.map((item, index) => {
            const content = (
              <div
                className={clsx(
                  "flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm select-none cursor-pointer",
                  "focus:bg-[#7b2a40]/10 focus:text-[#7b2a40]",
                  !item.disabled && "hover:bg-gray-50",
                  item.disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="flex items-center gap-2">
                  {item.icon && <span className="text-[#7b2a40]">{item.icon}</span>}
                  <span>{item.label}</span>
                </div>
                {selectedValue && selectedValue === item.value && (
                  <Check size={16} className="text-[#7b2a40]" />
                )}
              </div>
            );

            if (item.href) {
              if (item.external)
                return (
                  <a
                    key={index}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {content}
                  </a>
                );

              return (
                <Link key={index} to={item.href} onClick={() => onSelect?.(item.value ?? "")}>
                  {content}
                </Link>
              );
            }

            return (
              <DropdownMenu.Item
                key={index}
                onSelect={() => !item.disabled && onSelect?.(item.value ?? "")}
                disabled={item.disabled}
              >
                {content}
              </DropdownMenu.Item>
            );
          })}

          <DropdownMenu.Arrow className="fill-gray-200" />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
